import { useEffect, useRef, useState } from 'react';

import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Mention, MentionsInput } from 'react-mentions';
import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { Button } from '../atoms/new-button';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useGetUsersInOrg } from '@/api/queries/useGetUsersInOrg';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { renderHighlightedTextWithLinks } from '@/helpers/textRendering';
import { MentionsInputStyleComments } from '@/styles';
import { FactComment } from '@/types';

// Interfaces
interface CommentPopupProps {
  comments: FactComment[];
  caseId: string;
  factId: string;
  commentsCount: number;
}

const CommentPopup = ({ comments, caseId, factId, commentsCount }: CommentPopupProps) => {
  // State
  const [value, setValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState<number>(commentsCount);
  const [loadingNewComment, setLoadingNewComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  const [localComments, setLocalComments] = useState<FactComment[]>(comments);

  const { user } = useUserContext();
  const currentUserId = user?.id;

  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  const buttonRef = useRef<HTMLDivElement>(null);

  // Data fetching
  const { getFetchConfig } = useGetFetchConfig();

  const { data: userData } = useGetUsersInOrg();

  const userDataForDropdown =
    userData && userData.length ? userData.map((user) => ({ id: user.id, display: user.email })) : [];

  // Comment operations
  const { mutateAsync: createComment } = useMutation(
    (newComment: { comment: string }) => {
      setLoadingNewComment(true);
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          comment: newComment.comment,
          url: window.location.href,
        },
      });
      return fetch(`${APIBaseChronos}/api/case/${caseId}/fact/${factId}/comments`, fetchConfig).then((res) => {
        return res.json();
      });
    },
    {
      onSuccess: (data) => {
        setLocalComments([...localComments, data]);
        setLoadingNewComment(false);
        setValue('');
        setCommentCount(commentCount + 1);
        toast.success('Comment added successfully');
      },
      onError: (error) => {
        setLoadingNewComment(false);
        console.error('Error posting comment:', error);
        toast.error('Failed to post comment. Please try again.');
      },
    },
  );

  const { mutate: editComment } = useMutation(
    (updatedComment: { comment: string }) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.PATCH,
        data: {
          content: updatedComment.comment,
        },
      });
      return fetch(`${APIBaseChronos}/api/case/${caseId}/comments/${editingCommentId}`, fetchConfig).then((res) =>
        res.json(),
      );
    },
    {
      onSuccess: (data) => {
        setLocalComments(localComments.map((comment) => (comment.id === editingCommentId ? data : comment)));
        setValue('');
        setEditingCommentId(null);
        toast.success('Comment updated successfully');
      },
      onError: (error) => {
        console.error('Error updating comment:', error);
        toast.error('Failed to update comment. Please try again.');
      },
    },
  );

  const { mutate: deleteComment } = useMutation(
    (commentId: string) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.DELETE,
      });
      return fetch(`${APIBaseChronos}/api/case/${caseId}/comments/${commentId}`, fetchConfig).then((res) => res.json());
    },
    {
      onSuccess: (data) => {
        setLocalComments(localComments.filter((comment) => comment.id !== data.id));
        setCommentCount(commentCount - 1);
        toast.success('Comment deleted successfully');
      },
      onError: (error) => {
        console.error('Error deleting comment:', error);
        toast.error('Failed to delete comment. Please try again.');
      },
    },
  );

  // Handlers
  const handleEditComment = (comment: FactComment) => {
    if (comment.createdById !== currentUserId) return; // Ensure only the user who created the comment can edit it
    setValue(comment.content);
    setEditingCommentId(comment.id);
    setIsOpen(true);
  };

  const handleSubmitCommentUpdate = () => {
    editComment({ comment: value });
  };

  const handleCancelUpdate = () => {
    setValue('');
    setEditingCommentId('');
  };

  const handleDeleteComment = (comment: FactComment) => {
    if (comment.createdById !== currentUserId) return;
    deleteComment(comment.id);
  };

  const handleSubmitComment = async () => {
    if (!value) return;
    await createComment({ comment: value });
    trackCustomEvent(TrackingEvents.CommentAddedToFact, { factId: factId });
  };

  // Effects
  useEffect(() => {
    setCommentCount(commentsCount);
  }, [commentsCount]);

  return (
    <div className="flex flex-row items-center justify-start text-xs font-medium">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div
            ref={buttonRef}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            className="relative flex h-full items-center gap-2 rounded border border-blue-200 bg-white/50 px-2 py-2 text-blue-500 hover:cursor-pointer hover:bg-white/70 hover:shadow-none"
          >
            {commentsCount ? `(${commentsCount})` : commentsCount}
            <FontAwesomeIcon icon={faComment} className="h-4 w-4" />
          </div>
        </PopoverTrigger>
        <PopoverContent side="left" align="start" className="w-[300px]">
          <div className="w-full">
            <div className="flex w-full flex-col items-start justify-center">
              <div className="max-h-96 w-full overflow-y-scroll">
                {localComments && localComments.length
                  ? localComments.map((comment) => {
                      return (
                        <div
                          key={comment.id}
                          className="mb-2 flex flex-col rounded border border-gray-100 p-2 text-xs shadow-sm"
                        >
                          <div className="text-sm font-medium">{comment?.createdBy?.email ?? ''}</div>
                          <div className="mb-1 text-xs text-gray-400">
                            {formatDate(comment.createdDate || Date.now())}
                          </div>
                          {editingCommentId !== comment.id ? (
                            <div className="text-gray-800">{renderHighlightedTextWithLinks(comment.content)}</div>
                          ) : (
                            // TODO: unify update & create comment to use same MentionsInput/Mention
                            <MentionsInput
                              value={value}
                              onChange={(e: any) => setValue(e.target.value)}
                              className="h-12 w-full rounded border"
                              placeholder={"Mention people using '@'"}
                              a11ySuggestionsListLabel={'Suggested mentions'}
                              style={MentionsInputStyleComments}
                            >
                              <Mention
                                data={userDataForDropdown}
                                trigger="@"
                                className="rounded-lg bg-gray-100 pb-0.5"
                              />
                            </MentionsInput>
                          )}
                          {comment.createdById === currentUserId && (
                            <div className="mt-2 flex flex-row gap-4">
                              {!editingCommentId && (
                                <>
                                  <Button
                                    size="sm"
                                    className="h-6 text-xs"
                                    variant="secondary"
                                    onClick={() => handleEditComment(comment)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-6 text-xs"
                                    variant="destructive"
                                    onClick={() => handleDeleteComment(comment)}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                              {editingCommentId === comment.id && (
                                <>
                                  <Button
                                    size="sm"
                                    className="h-6 text-xs"
                                    variant="secondary"
                                    onClick={handleSubmitCommentUpdate}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    className="h-6 text-xs"
                                    size="sm"
                                    variant="destructive"
                                    onClick={handleCancelUpdate}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  : null}
              </div>
              {!editingCommentId && (
                <>
                  <MentionsInput
                    value={value}
                    onChange={(e: any) => setValue(e.target.value)}
                    className="h-12 w-full rounded border"
                    placeholder={"Mention people using '@'"}
                    a11ySuggestionsListLabel={'Suggested mentions'}
                    style={MentionsInputStyleComments}
                  >
                    <Mention data={userDataForDropdown} trigger="@" className="rounded-lg bg-gray-100 pb-0.5" />
                  </MentionsInput>
                  <div className="mt-4 mb-2 flex w-full flex-row justify-between">
                    <Button size="sm" className="text-xs" variant="primary" onClick={handleSubmitComment}>
                      {loadingNewComment ? <FontAwesomeIcon icon={faCircleNotch} className="fa-spin mr-1" /> : null}
                      Comment
                    </Button>
                    <Button size="sm" className="text-xs" variant="secondary" onClick={() => setIsOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CommentPopup;
